import { ReactNode, useEffect, useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { MtxButton, MtxGrid } from '@meilleurtaux/mtp-uikit-react';
import { useTranslation } from 'react-i18next';
import { NumberFormatValues } from 'react-number-format';
import { useLocation, useNavigate } from 'react-router-dom';

import Breadcrumb from '@/components/Breadcrumb/Breadcrumb';
import CardIbanGestionPilotee from '@/components/CardIbanGestionPilotee/CardIbanGestionPilotee';
import ChipsRadio from '@/components/chipsRadio/chipsRadio';
import FormTextError from '@/components/formTextError/formTextError';
import { Loader } from '@/components/Loader/loader';
import ModaleDefaut from '@/components/ModaleDefaut/ModaleDefaut';
import { NumericField } from '@/components/NumericField';
import TaxDeduction from '@/components/TaxDeduction/TaxDeduction';
import TextInput from '@/components/TextInput/TextInput';
import { NFD_DIACRITICS, SPACE_CHARACTERS } from '@/constants/regex';
import { ROUTE } from '@/constants/route';
import { useAuth } from '@/hooks/useAuth';
import { useFaireVersement } from '@/hooks/useFaireVersement';
import { useFaireVersementJustificatifsValidationUniversign } from '@/hooks/useFaireVersementJustificatifsValidationUniversign';
import { TRACKING_INSTANCE } from '@/hooks/useTracking/useTrackingInit/useTrackingInit';
import { BreadcrumbItem } from '@/services/types/Breadcrumb';
import { FaireVersementResponse } from '@/types/parcoursVersementTypes';
import { log } from '@/utils/functions/logger/logger';

import styles from './styles.module.css';

const mainObjectiveLifeInsurance = [
  {
    label: 'Optimiser ma transmission',
    value: 'cpteObjectif006',
  },
  {
    label: 'Disposer de revenus complémentaires',
    value: 'cpteObjectif001',
  },
  {
    label: 'Faire fructifier mon épargne',
    value: 'cpteObjectif002',
  },
  {
    label: 'Financer un projet (études, acquisition)',
    value: 'cpteObjectif003',
  },
];
const mainObjectivePER = [
  {
    label: 'Optimiser ma transmission',
    value: 'cpteObjectif006',
  },
  {
    label: 'Disposer de revenus complémentaires pour ma retraite',
    value: 'cpteObjectif001',
  },
  {
    label: 'Faire fructifier mon épargne et réduire ma fiscalité',
    value: 'cpteObjectif002',
  },
  {
    label: 'Financer un projet (achat de ma résidence principale)',
    value: 'cpteObjectif003',
  },
];
interface DepositDataInterface {
  idSouscription: number;
  depositType: string;
  depositAmount: number;
  mainObjective: string;
  fundsOrigin: string;
  otherFundsOrigin: string;
  fundsOriginDate: string;
  fundsPlacement: string;
  ibanSelected: string;
  per: boolean;
  taxDeduction: boolean;
}

interface RouteParams {
  ':familleContrat': string;
  ':typeContrat': string;
}

export const FaireVersement: React.FC = () => {
  const { t } = useTranslation();
  const depositData = JSON.parse(sessionStorage.parcoursVersementData);

  const { user } = useAuth();
  const [showLoader, setShowLoader] = useState(false);

  const navigate = useNavigate();
  const { state } = useLocation();
  const fetchedData = state?.data;
  const isEligibleForTaxDeduction =
    fetchedData?.tokenDecode?.isEligibileDeductionFiscale ?? depositData.isEligibleDeductionFiscale;

  const ibans = depositData.cbs ? depositData.cbs : fetchedData.tokenDecode.cbs;

  const otherOrigins = new Set(['gains-aux-jeux', 'heritage-donation', 'cession-actif', 'autre-a-preciser']);

  const makeDeposit = useFaireVersement();
  const depositDocumentsValidationUniversign = useFaireVersementJustificatifsValidationUniversign();

  let initialMainObjective = '';
  if (depositData.objectifPrincipal) {
    initialMainObjective = depositData.objectifPrincipal;
  } else if (state?.objPrincipal !== null) {
    initialMainObjective = state?.objPrincipal;
  }
  // Modal content
  const modalContent = (
    <div className={styles.taxDeductionModal}>
      <p>{t('deposit.modalWording1')}</p>
      <p>{t('deposit.modalWording2')}</p>
    </div>
  );

  const [depositFormData, setDepositFormData] = useState<DepositDataInterface>({
    idSouscription: depositData.idSouscription ?? fetchedData.idSouscription,
    depositType: 'Ponctuel',
    depositAmount: depositData.montantVersement ?? 0,
    mainObjective: initialMainObjective,
    fundsOrigin: depositData.origineCapitaux ?? '',
    otherFundsOrigin:
      depositData.autreOrigineFonds && otherOrigins.has(depositData.origineCapitaux)
        ? depositData.autreOrigineFonds
        : '',
    fundsOriginDate:
      depositData.dateOrigineFonds && otherOrigins.has(depositData.origineCapitaux) ? depositData.dateOrigineFonds : '',
    fundsPlacement:
      depositData.placementDesFonds && otherOrigins.has(depositData.origineCapitaux)
        ? depositData.placementDesFonds
        : '',
    ibanSelected: depositData.ibanSelect || '',
    per: depositData.per,
    taxDeduction: depositData.deductionFiscale ?? false,
  });

  const contractType = depositData.per ? 'PER' : 'assu-vie';

  const [showSelectPlaceholder, setShowSelectPlaceholder] = useState(depositFormData.mainObjective === '');

  const [confirmSituation, setConfirmSituation] = useState(depositData.confirmSituation);

  const [amountError, setAmountError] = useState(false);
  const [amountErrorText, setAmountErrorText] = useState('');

  const [amountString, setAmountString] = useState(
    depositData.montantVersement === 0 ? '' : depositData.montantVersement.toString()
  );

  const [textErrorDate, setTextErrorDate] = useState('');
  const [errorDate, setErrorDate] = useState(false);

  const amountRegex = /^-?\d+([.,]\d{1,2})?$/;

  const [didChooseDeduction, setDidChooseDeduction] = useState(depositData.deductionFiscale !== null);

  const [submitted, setSubmitted] = useState(false);

  const [showFundsOriginText, setShowFundsOriginText] = useState(!!depositFormData.fundsPlacement);

  const showTaxDeduction = depositData.per;
  const [showTaxDeductionModal, setShowTaxDeductionModal] = useState(false);

  const mapRoute: RouteParams = {
    ':familleContrat':
      depositData.contratFamille.toLowerCase() === 'assurancevie'
        ? 'assurance-vie'
        : depositData.contratFamille.toLowerCase(),
    ':typeContrat': depositData.contratNomProduit
      .toLowerCase()
      .replaceAll(' ', '-')
      .normalize('NFD')
      .replaceAll(NFD_DIACRITICS, ''),
  };

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: 'Choisir un support', path: ROUTE.SELECTOPERATION },
    { label: 'Faire un versement', path: ROUTE.DEPOSIT },
  ];

  const openModal = (modal: string) => {
    document.body.classList.add('modal-open');

    if (modal === 'showTaxDeductionModal') {
      setShowTaxDeductionModal(true);
    }
  };

  const closeModal = () => {
    setShowTaxDeductionModal(false);
  };

  const handleFundsOriginDate = (e: React.ChangeEvent<HTMLInputElement>) => {
    const today = new Date();
    setDepositFormData({
      ...depositFormData,
      fundsOriginDate: e.target.value,
    });

    if (Math.floor((today.getTime() - new Date(e.target.value).getTime()) / (1000 * 60 * 60 * 24)) > 182)
      setShowFundsOriginText(true);
    else setShowFundsOriginText(false);
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setDepositFormData({
      ...depositFormData,
      mainObjective: e.currentTarget.value,
    });

    if (showSelectPlaceholder) setShowSelectPlaceholder(false);
  };

  const handleAmountChange = ({ floatValue = 0, formattedValue }: NumberFormatValues) => {
    setDepositFormData({
      ...depositFormData,
      depositAmount: floatValue,
    });

    sessionStorage.setItem(
      'parcoursVersementData',
      JSON.stringify({
        ...depositData,
        montantVersement: floatValue,
      })
    );

    setAmountString(formattedValue);
  };

  const scrollToInputError = (anchorId: string) => {
    const anchor = document.getElementById(anchorId);
    anchor?.scrollIntoView({ behavior: 'smooth' });
  };

  const checkFormData = () => {
    const sanitizedAmount = amountString.replaceAll(SPACE_CHARACTERS, '');

    if (!amountRegex.test(sanitizedAmount)) {
      setAmountError(true);
      setAmountErrorText('Veuillez renseigner le montant que vous souhaitez verser');
      scrollToInputError('faire-versement-anchor-montant');
      return false;
    } else if (depositFormData.depositAmount < 100) {
      setAmountError(true);
      if (Number.parseFloat(sanitizedAmount) === 0 || Number.isNaN(Number.parseFloat(sanitizedAmount)))
        setAmountErrorText('Veuillez renseigner le montant que vous souhaitez verser');
      else setAmountErrorText('Le montant minimum est de 100€');
      scrollToInputError('faire-versement-anchor-montant');
      return false;
    }
    setAmountError(false);

    if (state?.objPrincipal === null && depositFormData.mainObjective === '') {
      scrollToInputError('faire-versement-anchor-objectif');
      return false;
    }

    if (depositFormData.fundsOrigin === '' || depositFormData.fundsOrigin === 'autres') {
      scrollToInputError('faire-versement-anchor-origine');
      return false;
    }

    if (otherOrigins.has(depositFormData.fundsOrigin) && depositFormData.fundsOriginDate === '') {
      setErrorDate(true);
      setTextErrorDate("Veuillez saisir la date d'acquisition des fonds");
      scrollToInputError('faire-versement-anchor-origine-date');
      return false;
    }

    if (
      otherOrigins.has(depositFormData.fundsOrigin) &&
      new Date().toISOString().split('T')[0] < depositFormData.fundsOriginDate
    ) {
      setErrorDate(true);
      setTextErrorDate('Veuillez rentrer une date valide');
      scrollToInputError('faire-versement-anchor-origine-date');
      return false;
    }
    setErrorDate(false);

    if (
      showFundsOriginText === true &&
      depositFormData.fundsPlacement === '' &&
      otherOrigins.has(depositFormData.fundsOrigin)
    ) {
      scrollToInputError('faire-versement-anchor-lieu-placement');
      return false;
    }

    if (depositFormData.fundsOrigin === 'autre-a-preciser' && depositFormData.otherFundsOrigin === '') {
      scrollToInputError('faire-versement-anchor-origine-textfield');
      return false;
    }

    if (depositFormData.ibanSelected === '') {
      scrollToInputError('faire-versement-anchor-ibans');
      return false;
    }

    if (showTaxDeduction && didChooseDeduction === false) {
      scrollToInputError('faire-versement-anchor-deduction-fiscale');
      return false;
    }

    if (confirmSituation === false) {
      scrollToInputError('faire-versement-anchor-confirmer-situation');
      return false;
    }

    return true;
  };

  const canSkipJustifs = (data: FaireVersementResponse) => {
    if (data.typeIdentite === null) {
      return false;
    }

    if (
      data.typeIdentite === 'cni' &&
      (data.validFile['justificatif-identite-1'] !== true ||
        data.validFile['justificatif-identite-1-recto'] !== true ||
        data.validFile['justificatif-identite-1-verso'] !== true)
    ) {
      return false;
    }

    if (
      data.typeIdentite === 'passeport' &&
      (data.validFile['justificatif-identite-1'] !== true || data.validFile['justificatif-identite-1-recto'] !== true)
    ) {
      return false;
    }

    if (
      data.validFile['justificatif-telephone'] !== true ||
      data.validFile['justificatif-origine_fonds'] === false ||
      data.validFile['justificatif-origine_fonds'] === true
    ) {
      return false;
    }

    return true;
  };

  const saveDepositToSession = (data: FaireVersementResponse) => {
    const { profilCalcul, profilClient, typeIdentite, validFile } = data;
    sessionStorage.setItem(
      'parcoursVersementData',
      JSON.stringify({
        ...depositData,
        idSouscription: depositFormData.idSouscription,
        typeVersement: depositFormData.depositType,
        objectifPrincipal: depositFormData.mainObjective,
        per: depositFormData.per,
        faireVersementResponse: {
          profilCalcul,
          profilClient,
          typeIdentite,
          validFile,
        },
      })
    );
  };

  const handleContinue = () => {
    setShowLoader(true);

    if (checkFormData()) {
      // Transform to API expected format
      const apiPayload = {
        idSouscription: depositFormData.idSouscription,
        typeVersement: depositFormData.depositType,
        montantVersement: depositFormData.depositAmount,
        objectifPrincipal: depositFormData.mainObjective,
        origineCapitaux: depositFormData.fundsOrigin,
        autreOrigineFonds: depositFormData.otherFundsOrigin,
        dateOrigineFonds: depositFormData.fundsOriginDate,
        placementDesFonds: depositFormData.fundsPlacement,
        ibanSelected: depositFormData.ibanSelected,
        per: depositFormData.per,
        deductionFiscale: depositFormData.taxDeduction,
      };

      makeDeposit.mutate(apiPayload, {
        onSuccess: (data: FaireVersementResponse) => {
          datadogRum.addAction('versement_soumis', {
            success: data.success,
            canGoToJustifs: data.canGoToJustifs,
          });

          const depositResponse = data;

          const fundsOrigin = depositFormData.fundsOrigin;

          if (data.canGoToJustifs === false) {
            saveDepositToSession(data);

            navigate(
              ROUTE.FAIREVERSEMENTVALIDATION.replaceAll(/:familleContrat|:typeContrat/gi, (matched) => {
                return mapRoute[matched as keyof RouteParams];
              }),
              {
                state: {
                  data,
                  origineFonds: fundsOrigin,
                  breadcrumbItems,
                  idSouscription: depositFormData.idSouscription,
                },
              }
            );
          } else if (canSkipJustifs(data)) {
            depositDocumentsValidationUniversign.mutate(depositFormData.idSouscription, {
              onSuccess: (data: FaireVersementResponse) => {
                datadogRum.addAction('versement_universign_valide', {
                  success: data.success,
                  emailUtilise: !!data.emailUtilise,
                  telephoneUtilise: !!data.telephoneUtilise,
                  coordonneesUtilise: data.coordonneesUtilise,
                  pieceLu: data.pieceLu,
                  infosNonCorrespondante: data.infosNonCorrespondante,
                  pieceExpire: data.pieceExpire,
                });

                saveDepositToSession(data);

                if (data.success === true) {
                  navigate(
                    ROUTE.FAIREVERSEMENTSIGNATURE.replaceAll(/:familleContrat|:typeContrat/gi, (matched) => {
                      return mapRoute[matched as keyof RouteParams];
                    }),
                    {
                      state: { data, breadcrumbItems, idSouscription: depositFormData.idSouscription },
                    }
                  );
                } else {
                  navigate(
                    ROUTE.FAIREVERSEMENTJUSTIFICATIFS.replaceAll(/:familleContrat|:typeContrat/gi, (matched) => {
                      return mapRoute[matched as keyof RouteParams];
                    }),
                    {
                      state: {
                        idSouscription: depositFormData.idSouscription,
                        data: depositResponse,
                        origineFonds: fundsOrigin,
                        breadcrumbItems,
                        universignErrorVersement: data,
                      },
                    }
                  );
                }
              },
              onError: (error) => {
                log.error(`DepositDocumentsValidationUniversign mutation failed: ${error}`);

                navigate(ROUTE.ERREUR);
              },
            });
          } else {
            saveDepositToSession(data);

            navigate(
              ROUTE.FAIREVERSEMENTJUSTIFICATIFS.replaceAll(/:familleContrat|:typeContrat/gi, (matched) => {
                return mapRoute[matched as keyof RouteParams];
              }),
              {
                state: {
                  idSouscription: depositFormData.idSouscription,
                  data,
                  origineFonds: fundsOrigin,
                  breadcrumbItems,
                },
              }
            );
          }
        },
        onError: (error) => {
          log.error(`MakeDeposit mutation failed: ${error}`);

          navigate(ROUTE.ERREUR);
        },
      });
    } else {
      setSubmitted(true);
      setShowLoader(false);
    }
  };

  const isFundsOriginRadioChecked = (fundsOriginValue: string) => {
    switch (fundsOriginValue) {
      case 'revenu':
        if (
          (depositFormData.fundsOrigin === '' && depositData.origineCapitaux === 'revenu') ||
          depositFormData.fundsOrigin === 'revenu'
        )
          return true;
        break;

      case 'epargne':
        if (
          (depositFormData.fundsOrigin === '' && depositData.origineCapitaux === 'epargne') ||
          depositFormData.fundsOrigin === 'epargne'
        )
          return true;
        break;

      case 'autres':
        if (
          ((depositData.origineCapitaux === 'autres' || otherOrigins.has(depositData.origineCapitaux)) &&
            depositFormData.fundsOrigin === '') ||
          depositFormData.fundsOrigin === 'autres' ||
          otherOrigins.has(depositFormData.fundsOrigin)
        )
          return true;
        break;

      case 'gains-aux-jeux':
        if (
          (depositFormData.fundsOrigin === '' && depositData.origineCapitaux === 'gains-aux-jeux') ||
          depositFormData.fundsOrigin === 'gains-aux-jeux'
        )
          return true;
        break;

      case 'heritage-donation':
        if (
          (depositFormData.fundsOrigin === '' && depositData.origineCapitaux === 'heritage-donation') ||
          depositFormData.fundsOrigin === 'heritage-donation'
        )
          return true;
        break;

      case 'cession-actif':
        if (
          (depositFormData.fundsOrigin === '' && depositData.origineCapitaux === 'cession-actif') ||
          depositFormData.fundsOrigin === 'cession-actif'
        )
          return true;
        break;

      case 'autre-a-preciser':
        if (
          (depositFormData.fundsOrigin === '' && depositData.origineCapitaux === 'autre-a-preciser') ||
          depositFormData.fundsOrigin === 'autre-a-preciser'
        )
          return true;
        break;

      default:
        return false;
    }
    return false;
  };

  if (
    Object.keys(ibans).length === 1 &&
    (depositFormData.ibanSelected === '' ||
      depositFormData.ibanSelected === null ||
      depositFormData.ibanSelected === undefined)
  ) {
    setDepositFormData({
      ...depositFormData,
      ibanSelected: Object.entries(ibans)[0][0],
    });
  }

  const displayIbans = () => {
    const IbanReactNode: ReactNode = Object.keys(ibans).map((key) => {
      let iban: string = ibans[key].iban.replaceAll(' ', '');
      const ibanToSend = iban;

      for (let i = 4; i < iban.length; i += 5) {
        iban = iban.slice(0, i) + ' ' + iban.slice(i);
      }

      iban = iban.replaceAll(' ', '\u00A0');

      return (
        <MtxGrid key={key} col={4} colXs={12} colSm={6} colMd={6} colLg={4} colXl={4}>
          <CardIbanGestionPilotee
            numIban={iban}
            nomBanque={ibans[key].banque}
            name="faire-versement-choix-iban"
            onChange={() => {
              setDepositFormData({
                ...depositFormData,
                ibanSelected: ibanToSend,
              });
              sessionStorage.setItem(
                'parcoursVersementData',
                JSON.stringify({
                  ...depositData,
                  ibanSelect: ibanToSend,
                })
              );
            }}
            isChecked={Object.keys(ibans).length === 1 || depositFormData.ibanSelected === ibanToSend}
          />
        </MtxGrid>
      );
    });
    return IbanReactNode;
  };

  useEffect(() => {
    if (user?.id && user.type && user.success) {
      TRACKING_INSTANCE.setTcVars({
        page_nom: 'faire un versement',
        page_template: 'espace client',
        user_id: user.id,
        user_client: user.type === 'Client',
        user_connexion: user.success,
        user_statut_contrat: sessionStorage.getItem('user_statut_contrat'),
        tunnel_produit: JSON.parse(sessionStorage.getItem('parcoursVersementData') || '').contratNomProduit,
        tunnel_metier: 'espace client placement',
        tunnel_sous_metier: JSON.parse(sessionStorage.getItem('parcoursVersementData') || '').contratFamille,
        tunnel_categorie_produit: 'parcours de versement',
      });
      void TRACKING_INSTANCE.triggerEvent('page', undefined, {
        event_id: 'page',
        event_categorie: 'affichage espace client',
        event_nom: 'faire un versement',
        event_page: 'faire un versement',
      });
      window.scrollTo({ top: 0, behavior: 'instant' });
    }

    if (!isEligibleForTaxDeduction) {
      setDepositFormData((prev) => ({
        ...prev,
        taxDeduction: false,
      }));
      setDidChooseDeduction(true);
    } else {
      sessionStorage.setItem(
        'parcoursVersementData',
        JSON.stringify({
          ...depositData,
          isEligibleForTaxDeduction: isEligibleForTaxDeduction,
        })
      );
    }
  }, [user?.id, user?.type, user?.success]);

  const handleSelectDeduction = (value: boolean) => {
    setDepositFormData({
      ...depositFormData,
      taxDeduction: value,
    });
    setDidChooseDeduction(true);
    sessionStorage.setItem(
      'parcoursVersementData',
      JSON.stringify({
        ...depositData,
        deductionFiscale: value,
      })
    );
  };

  return (
    <MtxGrid className={styles.depositBox}>
      {showLoader && <Loader />}

      {showTaxDeductionModal && (
        <ModaleDefaut
          title="Qu’est-ce que la déduction fiscale&nbsp;?"
          content={modalContent}
          btnLabel="J'ai compris"
          btnVariant="primary"
          btnSize="large"
          btnFullwidth={false}
          onClickBtn={() => closeModal()}
          onClickClose={() => closeModal()}
          mobileStyle="stick-to-bottom"
          size="large"
        />
      )}

      <MtxGrid className={styles.depositHeader} row>
        <MtxGrid col={12} colXs={12} colSm={12} colMd={12} colLg={12} colXl={12}>
          <Breadcrumb items={breadcrumbItems} />
          <h1>{t('deposit.title')}</h1>
          <p>{t('deposit.subtitle')}</p>
        </MtxGrid>
      </MtxGrid>
      <div>
        <MtxGrid row>
          <MtxGrid
            className={`${styles.inputTopLabel} ${styles.topLabelFirst}`}
            col={12}
            colXs={12}
            colSm={12}
            colMd={12}
            colLg={12}
            colXl={12}
          >
            <label htmlFor="type-versement">{t('deposit.label')}</label>
          </MtxGrid>

          <MtxGrid col={5} colXs={12} colSm={8} colMd={8} colLg={5} colXl={5}>
            <div className={`${styles.depositAmountInput} ${styles.depositInput} ${styles.inputSuccess}`}>
              <span>{depositFormData.depositType}</span>
              <i className="totem-icon totem-icon-check-circle" />
            </div>
          </MtxGrid>
        </MtxGrid>

        <MtxGrid row>
          <div className="scroll-anchor" id="faire-versement-anchor-montant"></div>
          <MtxGrid col={5} colXs={12} colSm={8} colMd={8} colLg={5} colXl={5}>
            <NumericField
              label="Montant du versement"
              labelFor="faire-versement-montant"
              placeholder="Minimum 100€"
              type="text"
              value={amountString}
              img="totem-icon-euro"
              textError={amountErrorText}
              isError={amountError}
              onValueChange={handleAmountChange}
            />
          </MtxGrid>
        </MtxGrid>

        {(state?.objPrincipal === null || state?.objPrincipal === '') && (
          <MtxGrid row>
            <div className="scroll-anchor" id="faire-versement-anchor-objectif"></div>
            <MtxGrid className={styles.inputTopLabel} col={12} colXs={12} colSm={12} colMd={12} colLg={12} colXl={12}>
              <label htmlFor="objectif-versement">{t('deposit.depositObjectiveLabel')}</label>
            </MtxGrid>

            <MtxGrid className={styles.selectWrapper} col={5} colXs={12} colSm={8} colMd={8} colLg={5} colXl={5}>
              <select
                className={
                  (submitted && depositFormData.mainObjective === '' ? 'text-input-input-error ' : '') +
                  styles.depositInput +
                  (showSelectPlaceholder ? ' select-placeholder' : '')
                }
                id="objectif-versement"
                value={depositFormData.mainObjective}
                onChange={(e) => {
                  handleSelectChange(e);
                  sessionStorage.setItem(
                    'parcoursVersementData',
                    JSON.stringify({
                      ...depositData,
                      objectifPrincipal: e.currentTarget.value,
                    })
                  );
                }}
                required
              >
                <option disabled hidden value="">
                  {t('deposit.selectObjectiveLabel')}
                </option>

                {contractType === 'assu-vie'
                  ? mainObjectiveLifeInsurance.map((value) => (
                      <option value={value.value} key={value.value}>
                        {value.label}
                      </option>
                    ))
                  : mainObjectivePER.map((value) => (
                      <option value={value.value} key={value.value}>
                        {value.label}
                      </option>
                    ))}
              </select>
            </MtxGrid>

            {submitted && depositFormData.mainObjective === '' && (
              <MtxGrid col={12} colXs={12} colSm={12} colMd={12} colLg={12} colXl={12}>
                <FormTextError text="Veuillez sélectionner l'une des options proposées" />
              </MtxGrid>
            )}
          </MtxGrid>
        )}

        <div className={styles.fundsOriginChips}>
          <div className="scroll-anchor" id="faire-versement-anchor-origine"></div>
          <MtxGrid row>
            <MtxGrid col={12} colXs={12} colSm={12} colMd={12} colLg={12} colXl={12}>
              <h3>{t('deposit.fundsOriginLabel')}</h3>
              <p className={styles.fundsOriginChipsText}>{t('deposit.fundsOriginRequirement')}</p>
            </MtxGrid>
          </MtxGrid>
          <div className={styles.fundsOriginChipsRadio}>
            <ChipsRadio
              size="large"
              label="Revenu"
              name="origine-capitaux"
              value="revenu"
              onChange={(e) => {
                setDepositFormData({
                  ...depositFormData,
                  fundsOrigin: e.currentTarget?.value,
                });
                sessionStorage.setItem(
                  'parcoursVersementData',
                  JSON.stringify({
                    ...depositData,
                    origineCapitaux: e.currentTarget.value,
                  })
                );
              }}
              isChecked={isFundsOriginRadioChecked('revenu')}
            />

            <ChipsRadio
              size="large"
              label="Épargne"
              name="origine-capitaux"
              value="epargne"
              onChange={(e) => {
                setDepositFormData({
                  ...depositFormData,
                  fundsOrigin: e.currentTarget?.value,
                });
                sessionStorage.setItem(
                  'parcoursVersementData',
                  JSON.stringify({
                    ...depositData,
                    origineCapitaux: e.currentTarget.value,
                  })
                );
              }}
              isChecked={isFundsOriginRadioChecked('epargne')}
            />

            <ChipsRadio
              size="large"
              label="Autre"
              name="origine-capitaux"
              value="autres"
              onChange={(e) => {
                setDepositFormData({
                  ...depositFormData,
                  fundsOrigin: e.currentTarget?.value,
                });
                sessionStorage.setItem(
                  'parcoursVersementData',
                  JSON.stringify({
                    ...depositData,
                    origineCapitaux: e.currentTarget.value,
                  })
                );
              }}
              isChecked={isFundsOriginRadioChecked('autres')}
            />
          </div>

          {submitted && depositFormData.fundsOrigin === '' && <FormTextError text={t('deposit.originError')} />}

          {(depositFormData.fundsOrigin === 'autres' ||
            depositFormData.fundsOrigin === 'gains-aux-jeux' ||
            depositFormData.fundsOrigin === 'heritage-donation' ||
            depositFormData.fundsOrigin === 'cession-actif' ||
            depositFormData.fundsOrigin === 'autre-a-preciser') && (
            <div className={`${styles.fundsOriginChipsRadioOthers} ${styles.fundsOriginChipsRadioOthersTop}`}>
              <ChipsRadio
                size="large"
                label="Gains aux jeux"
                name="origine-capitaux-autres"
                value="gains-aux-jeux"
                onChange={(e) => {
                  setDepositFormData({
                    ...depositFormData,
                    fundsOrigin: e.currentTarget?.value,
                  });
                  sessionStorage.setItem(
                    'parcoursVersementData',
                    JSON.stringify({
                      ...depositData,
                      origineCapitaux: e.currentTarget.value,
                    })
                  );
                }}
                isChecked={isFundsOriginRadioChecked('gains-aux-jeux')}
              />

              <ChipsRadio
                size="large"
                label="Héritage / donation"
                name="origine-capitaux-autres"
                value="heritage-donation"
                onChange={(e) => {
                  setDepositFormData({
                    ...depositFormData,
                    fundsOrigin: e.currentTarget?.value,
                  });
                  sessionStorage.setItem(
                    'parcoursVersementData',
                    JSON.stringify({
                      ...depositData,
                      origineCapitaux: e.currentTarget.value,
                    })
                  );
                }}
                isChecked={isFundsOriginRadioChecked('heritage-donation')}
              />

              <ChipsRadio
                size="large"
                label="Cession d'actif"
                name="origine-capitaux-autres"
                value="cession-actif"
                onChange={(e) => {
                  setDepositFormData({
                    ...depositFormData,
                    fundsOrigin: e.currentTarget?.value,
                  });
                  sessionStorage.setItem(
                    'parcoursVersementData',
                    JSON.stringify({
                      ...depositData,
                      origineCapitaux: e.currentTarget.value,
                    })
                  );
                }}
                isChecked={isFundsOriginRadioChecked('cession-actif')}
              />

              <ChipsRadio
                size="large"
                label="Autre à préciser"
                name="origine-capitaux-autres"
                value="autre-a-preciser"
                onChange={(e) => {
                  setDepositFormData({
                    ...depositFormData,
                    fundsOrigin: e.currentTarget?.value,
                  });
                  sessionStorage.setItem(
                    'parcoursVersementData',
                    JSON.stringify({
                      ...depositData,
                      origineCapitaux: e.currentTarget.value,
                    })
                  );
                }}
                isChecked={isFundsOriginRadioChecked('autre-a-preciser')}
              />
            </div>
          )}

          {submitted && depositFormData.fundsOrigin === 'autres' && <FormTextError text={t('deposit.originError')} />}

          {(depositFormData.fundsOrigin === 'gains-aux-jeux' ||
            depositFormData.fundsOrigin === 'heritage-donation' ||
            depositFormData.fundsOrigin === 'cession-actif' ||
            depositFormData.fundsOrigin === 'autre-a-preciser') && (
            <div>
              <MtxGrid row className={styles.fundsOriginChipsRadioOthers}>
                <div className="scroll-anchor" id="faire-versement-anchor-origine-date"></div>
                <MtxGrid col={5} colXs={12} colSm={8} colMd={8} colLg={5} colXl={5}>
                  <TextInput
                    label="Date d'acquisition des fonds"
                    labelFor=""
                    type="date"
                    value={depositFormData.fundsOriginDate}
                    textError={textErrorDate}
                    isError={errorDate}
                    onChange={(e) => {
                      handleFundsOriginDate(e as React.ChangeEvent<HTMLInputElement>);
                      sessionStorage.setItem(
                        'parcoursVersementData',
                        JSON.stringify({
                          ...depositData,
                          dateOrigineFonds: e.currentTarget.value,
                        })
                      );
                    }}
                  />
                </MtxGrid>
              </MtxGrid>

              {showFundsOriginText && (
                <MtxGrid row className={styles.fundsOriginChipsRadioOthers}>
                  <div className="scroll-anchor" id="faire-versement-anchor-lieu-placement"></div>
                  <MtxGrid col={5} colXs={12} colSm={8} colMd={8} colLg={5} colXl={5}>
                    <textarea
                      placeholder="Indiquez-nous où ont été placés ces fonds jusqu'à maintenant. Ex : Sur un livret d'épargne ou sur un compte courant. (140 caractères maximum)"
                      maxLength={140}
                      className={
                        (submitted && depositFormData.fundsPlacement === '' && 'text-input-input-error') +
                        ' input-form-rappel'
                      }
                      onChange={(e) => {
                        setDepositFormData({
                          ...depositFormData,
                          fundsPlacement: e.target.value,
                        });
                        sessionStorage.setItem(
                          'parcoursVersementData',
                          JSON.stringify({
                            ...depositData,
                            placementDesFonds: e.currentTarget.value,
                          })
                        );
                      }}
                      value={depositFormData.fundsPlacement}
                    ></textarea>

                    {submitted && depositFormData.fundsPlacement === '' && (
                      <FormTextError text="Veuillez préciser où ont été placés vos fonds" />
                    )}
                  </MtxGrid>
                </MtxGrid>
              )}

              {depositFormData.fundsOrigin === 'autre-a-preciser' && (
                <MtxGrid row className={styles.fundsOriginChipsRadioOthers}>
                  <div className="scroll-anchor" id="faire-versement-anchor-origine-textfield"></div>
                  <MtxGrid col={5} colXs={12} colSm={8} colMd={8} colLg={5} colXl={5}>
                    <textarea
                      placeholder="Indiquez-nous l'origine de vos capitaux"
                      className={
                        (submitted && depositFormData.otherFundsOrigin === '' && 'text-input-input-error') +
                        ' input-form-rappel'
                      }
                      onChange={(e) => {
                        setDepositFormData({
                          ...depositFormData,
                          otherFundsOrigin: e.target.value,
                        });
                        sessionStorage.setItem(
                          'parcoursVersementData',
                          JSON.stringify({
                            ...depositData,
                            autreOrigineFonds: e.currentTarget.value,
                          })
                        );
                      }}
                      value={depositFormData.otherFundsOrigin}
                    ></textarea>
                    {submitted && depositFormData.otherFundsOrigin === '' && (
                      <FormTextError text="Veuillez préciser l'origine de vos fonds" />
                    )}
                  </MtxGrid>
                </MtxGrid>
              )}
            </div>
          )}
        </div>

        <div>
          <MtxGrid row>
            <div className="scroll-anchor" id="faire-versement-anchor-ibans"></div>
            <MtxGrid col={12} colXs={12} colSm={12} colMd={12} colLg={12} colXl={12}>
              <p className={styles.ibanChoiceText}>{t('deposit.debitAccountLabel')}</p>
            </MtxGrid>
          </MtxGrid>

          <MtxGrid row className={styles.ibanRow}>
            {displayIbans()}
          </MtxGrid>

          {submitted && depositFormData.ibanSelected === '' && (
            <FormTextError text="Veuillez sélectionner l'une des options proposées" />
          )}
        </div>

        {showTaxDeduction && (
          <TaxDeduction
            isEligibleForTaxDeduction={isEligibleForTaxDeduction}
            isTaxDeductionSelected={depositFormData.taxDeduction}
            didChooseDeduction={didChooseDeduction}
            submitted={submitted}
            onSelectDeduction={handleSelectDeduction}
            onOpenInfoModal={() => openModal('showTaxDeductionModal')}
          />
        )}

        <div className="scroll-anchor" id="faire-versement-anchor-confirmer-situation"></div>
        <div className={styles.bottomCheckbox}>
          <label htmlFor="situation-inchangee" className={styles.unchangedSituationBox}>
            <input
              type="checkbox"
              id="situation-inchangee"
              className={styles.unchangedSituation}
              onChange={(e) => {
                setConfirmSituation(e.currentTarget.checked);
                sessionStorage.setItem(
                  'parcoursVersementData',
                  JSON.stringify({
                    ...depositData,
                    confirmSituation: e.currentTarget.checked,
                  })
                );
              }}
            />
            <div>{confirmSituation && <i className="totem-icon totem-icon-check" />}</div>
            {t('deposit.situationConfirmationLabel')}
          </label>
          {submitted && confirmSituation === false && <FormTextError text={t('deposit.confirmationError')} />}
        </div>

        <MtxGrid className={styles.confirmButton} row>
          <MtxGrid col={2} colXs={12} colSm={6} colMd={6} colLg={4} colXl={4}>
            <MtxButton onClick={handleContinue} variant="primary" size="large" disabled={false} fullWidth={true}>
              <div>{t('common.continue')}</div>
            </MtxButton>
          </MtxGrid>
        </MtxGrid>
      </div>
    </MtxGrid>
  );
};
